import { DocumentMapping, DynamicDocumentInstructions } from "@/configuration/document-mappings/types";
import { BALANCE_HEALTH_MAPPINGS } from "@/configuration/groups/balance-health/generated/document-mappings";
import {
  ASSIGNEE,
  BhFaxLine,
  getBhFaxLine,
  getRoutingFieldsFromReviewedContent,
  resolveBalanceHealthRouting,
} from "@/configuration/groups/balance-health/routing";
import { buildPhysicianNameIndex, matchPhysicianByName } from "@/configuration/physician-roster-name-match";
import {
  AutoIntakeEligibilityRequirement,
  type AutoIntakeEligibilityRequirements,
} from "@/src/lib/auto-intake/auto-intake-eligibility-requirements";
import { autoIntakeRule, type AutoIntakeRule } from "@/src/lib/auto-intake/auto-intake-rule";
import { DocRoutingStrategy } from "@/src/lib/constants";
import { logger } from "@/src/lib/logging/logger";
import {
  RC_BALANCE_HEALTH_FAX_NUMBER,
  RC_BALANCE_HEALTH_FAX_NUMBER_2,
  RC_BALANCE_HEALTH_ARTISAN_FAX_NUMBER,
  RC_BALANCE_HEALTH_DESERT_FAX_NUMBER,
  RC_BALANCE_HEALTH_IFAC_FAX_NUMBER,
  RC_BALANCE_HEALTH_WFAI_FAX_NUMBER,
  RC_BALANCE_HEALTH_FCC_FAX_NUMBER,
  RC_BALANCE_HEALTH_FAC_ROCKIES_FAX_NUMBER,
  RC_BALANCE_HEALTH_1F2F_FAX_NUMBER,
  RC_BALANCE_HEALTH_FASWM_FAX_NUMBER,
  RC_BALANCE_HEALTH_LVFAC_FAX_NUMBER,
  RC_BALANCE_HEALTH_UFAI_FAX_NUMBER,
  RC_BALANCE_HEALTH_PRECISION_FAX_NUMBER,
  RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  RC_BALANCE_HEALTH_SFAC_FAX_NUMBER,
  RC_BALANCE_HEALTH_PPO_FAX_NUMBER,
  RC_BALANCE_HEALTH_VPG_FAX_NUMBER,
} from "@/src/lib/ringcentral";
import type { StepOutputs } from "@/src/lib/workflows/steps/step-outputs";
import { Classification, ClassificationCategory, ClassificationSection } from "@/src/models/constants/classifications";
import { FaxLocationMapping } from "@/src/models/constants/fax-location";
import { Workflow, WorkflowType } from "@/src/models/constants/group";
import { Specialty } from "@/src/models/constants/specialty";
import { SchedulingPriority } from "@/src/models/documents/referral";
import { Group } from "@/src/models/group";
import { Patient, isPatientNotInEhr } from "@/src/models/patient";
import { Section, requireUploadClassification } from "@/src/models/section";
import { Task } from "@/src/models/task";

export const BALANCE_HEALTH_AUTO_INTAKE_RULES: AutoIntakeRule[] = [
  autoIntakeRule(Classification.LAB_RESULTS, 0.95),
  autoIntakeRule(Classification.MEDICAL_HISTORY_HOME_HEALTH, 0.5),
  autoIntakeRule(Classification.IMAGING_RESULTS, 0.9),
  autoIntakeRule(Classification.IMAGING_RESULTS_MRI, 0.5),
  autoIntakeRule(Classification.REFERRAL, 0.95),
  autoIntakeRule(Classification.PROGRESS_NOTE, 0.9),
  autoIntakeRule(Classification.PRIOR_AUTHORIZATION_PROCEDURE_RESPONSE),
];

// Required-field validation always runs regardless of the rate above, so a section with a
// missing required field (e.g. receivingProvider) never auto-intakes. This additionally blocks
// auto-intake when a provider field's NPI wasn't successfully matched, even if the raw text is
// present — see checkBlockEligibilityRequirements.
export const BALANCE_HEALTH_AUTO_INTAKE_ELIGIBILITY_REQUIREMENTS: AutoIntakeEligibilityRequirements = [
  AutoIntakeEligibilityRequirement.MATCHED_PROVIDER_NPI,
  AutoIntakeEligibilityRequirement.GEOCODED_LOCATION,
];

const BALANCE_HEALTH_HR_EMAIL = "Humanresources@weil4feet.com";
const BALANCE_HEALTH_SOCAL_HR_EMAIL = "HR@balancehealth.com";

// Sentinel that resolveAssignee() (workflow-steps.ts) maps to the receiving provider.
// Temporarily routing MA + call-center destinations here so CloudCruise assigns to the physician.
const ASSIGN_TO_PHYSICIAN = "Physician";
const ASSIGNEE_THOMPSON_SHAWN = "Thompson, Shawn";

export const BALANCE_HEALTH_UTAH_FAX_LINE = RC_BALANCE_HEALTH_FAX_NUMBER;
export const BALANCE_HEALTH_SOCAL_FAX_LINE = RC_BALANCE_HEALTH_FAX_NUMBER_2;
export const BALANCE_HEALTH_ARTISAN_FAX_LINE = RC_BALANCE_HEALTH_ARTISAN_FAX_NUMBER;
export const BALANCE_HEALTH_DESERT_FAX_LINE = RC_BALANCE_HEALTH_DESERT_FAX_NUMBER;
export const BALANCE_HEALTH_IFAC_FAX_LINE = RC_BALANCE_HEALTH_IFAC_FAX_NUMBER;
export const BALANCE_HEALTH_WFAI_FAX_LINE = RC_BALANCE_HEALTH_WFAI_FAX_NUMBER;
export const BALANCE_HEALTH_FCC_FAX_LINE = RC_BALANCE_HEALTH_FCC_FAX_NUMBER;
export const BALANCE_HEALTH_FAC_ROCKIES_FAX_LINE = RC_BALANCE_HEALTH_FAC_ROCKIES_FAX_NUMBER;
export const BALANCE_HEALTH_1F2F_FAX_LINE = RC_BALANCE_HEALTH_1F2F_FAX_NUMBER;
export const BALANCE_HEALTH_FASWM_FAX_LINE = RC_BALANCE_HEALTH_FASWM_FAX_NUMBER;
export const BALANCE_HEALTH_LVFAC_FAX_LINE = RC_BALANCE_HEALTH_LVFAC_FAX_NUMBER;
export const BALANCE_HEALTH_UFAI_FAX_LINE = RC_BALANCE_HEALTH_UFAI_FAX_NUMBER;
export const BALANCE_HEALTH_PRECISION_FAX_LINE = RC_BALANCE_HEALTH_PRECISION_FAX_NUMBER;
export const BALANCE_HEALTH_BAFC_FAX_LINE = RC_BALANCE_HEALTH_BAFC_FAX_NUMBER;
export const BALANCE_HEALTH_SFAC_FAX_LINE = RC_BALANCE_HEALTH_SFAC_FAX_NUMBER;
export const BALANCE_HEALTH_PPO_FAX_LINE = RC_BALANCE_HEALTH_PPO_FAX_NUMBER;
export const BALANCE_HEALTH_VPG_FAX_LINE = RC_BALANCE_HEALTH_VPG_FAX_NUMBER;

const BALANCE_HEALTH_DEPARTMENT_NAMES: Record<string, string> = {
  [BALANCE_HEALTH_UTAH_FAX_LINE]: "Utah",
  [BALANCE_HEALTH_SOCAL_FAX_LINE]: "SoCal",
  [BALANCE_HEALTH_ARTISAN_FAX_LINE]: "Artisan",
  [BALANCE_HEALTH_DESERT_FAX_LINE]: "Desert",
  [BALANCE_HEALTH_IFAC_FAX_LINE]: "IFAC",
  [BALANCE_HEALTH_WFAI_FAX_LINE]: "WFAI",
  [BALANCE_HEALTH_FCC_FAX_LINE]: "FCC",
  [BALANCE_HEALTH_FAC_ROCKIES_FAX_LINE]: "FAC Rockies",
  [BALANCE_HEALTH_1F2F_FAX_LINE]: "1F2F",
  [BALANCE_HEALTH_FASWM_FAX_LINE]: "FASWM",
  [BALANCE_HEALTH_LVFAC_FAX_LINE]: "LVFAC",
  [BALANCE_HEALTH_UFAI_FAX_LINE]: "UFAI",
  [BALANCE_HEALTH_PRECISION_FAX_LINE]: "Precision",
  [BALANCE_HEALTH_BAFC_FAX_LINE]: "BAFC",
  [BALANCE_HEALTH_SFAC_FAX_LINE]: "SFAC",
  [BALANCE_HEALTH_PPO_FAX_LINE]: "PPO",
  [BALANCE_HEALTH_VPG_FAX_LINE]: "VPG",
};

// Full ModMed facility names keyed by inbound fax line — used by the sync box (Staff Location)
// and by the CloudCruise automation (facility field).
export const BALANCE_HEALTH_FACILITY_NAMES: Record<string, string> = {
  [BALANCE_HEALTH_UTAH_FAX_LINE]: "UT Salt Lake City 400 W",
  [BALANCE_HEALTH_SOCAL_FAX_LINE]: "SoCal Foot Care PC",
  [BALANCE_HEALTH_ARTISAN_FAX_LINE]: "Artisan Foot and Ankle Podiatric Specialists",
  [BALANCE_HEALTH_DESERT_FAX_LINE]: "PNC Podiatry of Arizona PC DBA Desert Foot & Ankle",
  [BALANCE_HEALTH_IFAC_FAX_LINE]: "WA IFAC Everett",
  [BALANCE_HEALTH_WFAI_FAX_LINE]: "Weil Foot and Ankle Institute, LLC",
  [BALANCE_HEALTH_FCC_FAX_LINE]: "Foot Care Center LLC",
  [BALANCE_HEALTH_FAC_ROCKIES_FAX_LINE]: "Foot and Ankle Center of the Rockies LLC",
  [BALANCE_HEALTH_1F2F_FAX_LINE]: "1Foot 2Foot Centre for Foot & Ankle Care",
  [BALANCE_HEALTH_FASWM_FAX_LINE]: "Foot & Ankle Specialists of West Michigan, PLLC",
  [BALANCE_HEALTH_LVFAC_FAX_LINE]: "NV Las Vegas - Flamingo",
  [BALANCE_HEALTH_UFAI_FAX_LINE]: "University Foot and Ankle Institute",
  [BALANCE_HEALTH_PRECISION_FAX_LINE]: "CA Precision Surgery Center",
  [BALANCE_HEALTH_BAFC_FAX_LINE]: "CA BAFC Alameda",
  [BALANCE_HEALTH_SFAC_FAX_LINE]: "CA BAFC Davis",
  [BALANCE_HEALTH_PPO_FAX_LINE]: "CA BAFC Folsom",
  [BALANCE_HEALTH_VPG_FAX_LINE]: "CA BAFC Modesto",
};

/**
 * Balance Health (ModMed) scheduling config path. Only LIVE is wired for now, and it points at an
 * empty ruleset ("no rules") — see rules/schedule.yaml. Sandbox stays off (returns null), so
 * scheduling is disabled there.
 */
export function getBalanceHealthScheduleConfigPath(): string | null {
  return "configuration/groups/balance-health/rules/schedule.yaml";
}

export function getBalanceHealthDepartmentIds(): string[] {
  return [
    BALANCE_HEALTH_UTAH_FAX_LINE,
    BALANCE_HEALTH_SOCAL_FAX_LINE,
    BALANCE_HEALTH_ARTISAN_FAX_LINE,
    BALANCE_HEALTH_DESERT_FAX_LINE,
    BALANCE_HEALTH_IFAC_FAX_LINE,
    BALANCE_HEALTH_WFAI_FAX_LINE,
    BALANCE_HEALTH_FCC_FAX_LINE,
    BALANCE_HEALTH_FAC_ROCKIES_FAX_LINE,
    BALANCE_HEALTH_1F2F_FAX_LINE,
    BALANCE_HEALTH_FASWM_FAX_LINE,
    BALANCE_HEALTH_LVFAC_FAX_LINE,
    BALANCE_HEALTH_UFAI_FAX_LINE,
    BALANCE_HEALTH_PRECISION_FAX_LINE,
    BALANCE_HEALTH_BAFC_FAX_LINE,
    BALANCE_HEALTH_SFAC_FAX_LINE,
    BALANCE_HEALTH_PPO_FAX_LINE,
    BALANCE_HEALTH_VPG_FAX_LINE,
  ];
}

export function getBalanceHealthDepartmentName(departmentId: string): string | null {
  return BALANCE_HEALTH_DEPARTMENT_NAMES[departmentId] ?? null;
}

const BALANCE_HEALTH_REFERRAL_CLASSIFICATIONS: Classification[] = [Classification.INTAKE, Classification.REFERRAL];

export const BALANCE_HEALTH_USER_IDS: string[] = [
  "c143c1b0-0c33-40bc-8e7c-911b40fdb374",
  "0589a50d-434a-4c13-bad0-9386d73d9007",
  "b32847f9-3fe2-435e-9526-9fa96d34df73",
  "da7e0145-e3dd-4ccb-87d9-b566debe2bfb",
  "017ae038-502c-4cba-9ae8-7b9aaa08b26a",
  "d35cf5ec-3df4-4d97-b5b0-88ebfd75e806",
  "55abac95-e65c-4a1a-8077-df25b0c1a2f4",
  "21443c57-4c3b-4f11-ad85-61cdea70ee21",
  "2fc92c3d-6313-4afb-b05e-cfa9fb08136f",
  "6241861a-3586-419f-a0f3-1d7d12690ec1",
];

export const BALANCE_HEALTH_MODMED_TASKS_URL =
  "https://balancehealth.ema.md/ema/web/practice/staff/#/practice/staff/tasks/manage/received";

export const BALANCE_HEALTH_TIMEZONE = "America/Denver";

// Fallback assignee when no receiving provider is resolved on a Balance Health
// section. ModMed assignee fields use "Lastname, Firstname" with a space.
export const BALANCE_HEALTH_RECEIVING_PROVIDER_FALLBACK = "User, Assort Health";

// ModMed Recall `recall_type` is selected by the inbound fax line.
export const BALANCE_HEALTH_RECALL_TYPE_BY_FAX_LINE: Record<string, string> = {
  [RC_BALANCE_HEALTH_FAX_NUMBER]: "Referral AFAC",
  [RC_BALANCE_HEALTH_FAX_NUMBER_2]: "Referral SCFC",
  [RC_BALANCE_HEALTH_ARTISAN_FAX_NUMBER]: "Referral AFAPS",
  [RC_BALANCE_HEALTH_DESERT_FAX_NUMBER]: "Referral DFAA",
  [RC_BALANCE_HEALTH_IFAC_FAX_NUMBER]: "Referral IFAC",
  [RC_BALANCE_HEALTH_WFAI_FAX_NUMBER]: "Referral WFAI",
  [RC_BALANCE_HEALTH_FCC_FAX_NUMBER]: "Referral TFCC",
  [RC_BALANCE_HEALTH_FAC_ROCKIES_FAX_NUMBER]: "Referral FACR",
  [RC_BALANCE_HEALTH_1F2F_FAX_NUMBER]: "Referral 1F2F",
  [RC_BALANCE_HEALTH_FASWM_FAX_NUMBER]: "Referral FASWM",
  [RC_BALANCE_HEALTH_LVFAC_FAX_NUMBER]: "Referral NVFAC",
  [RC_BALANCE_HEALTH_UFAI_FAX_NUMBER]: "Referral UFAI",
  [RC_BALANCE_HEALTH_PRECISION_FAX_NUMBER]: "Referral UFAI",
  [RC_BALANCE_HEALTH_BAFC_FAX_NUMBER]: "Referral BAFC",
  [RC_BALANCE_HEALTH_SFAC_FAX_NUMBER]: "Referral SFAC",
  [RC_BALANCE_HEALTH_PPO_FAX_NUMBER]: "Referral PPO",
  [RC_BALANCE_HEALTH_VPG_FAX_NUMBER]: "Referral VPG",
};

const BALANCE_HEALTH_AUTOCLOSE_RATE_BY_FAX_LINE: Record<string, number> = {
  [BALANCE_HEALTH_UTAH_FAX_LINE]: 0.95, // AFAC
  [BALANCE_HEALTH_SOCAL_FAX_LINE]: 0.5, // SoCal
  [BALANCE_HEALTH_WFAI_FAX_LINE]: 0.5, // WFAI
};

export function getBalanceHealthAutoCloseSamplingRate(faxRecipientNumber: string | null): number {
  if (!faxRecipientNumber) return 0;

  return BALANCE_HEALTH_AUTOCLOSE_RATE_BY_FAX_LINE[faxRecipientNumber] ?? 0;
}

export function getBalanceHealthDocAutoCloseRate(_classification: Classification): number {
  return 0.95;
}

// Classifications whose uploaded doc attaches to an existing ModMed task instead of creating a new one;
// the task type ("Sign"/"Review") comes from the mapping's additionalWorkflow.
const BALANCE_HEALTH_ATTACH_DOC_CLASSIFICATIONS: ReadonlySet<Classification> = new Set([
  Classification.DME_ORDER,
  Classification.IMAGING_RESULTS_MRI,
]);

export function isBalanceHealthAttachDocClassification(section: Section): boolean {
  const classification = requireUploadClassification(section);

  return BALANCE_HEALTH_ATTACH_DOC_CLASSIFICATIONS.has(classification);
}

// ModMed prefixes the Task description with the uppercased task type ("SIGN: ..." / "REVIEW: ..."),
// which maps 1:1 to our mapping's Task Type; non-match falls back to creating a new task.
export function isBalanceHealthAttachDocTaskMatch(section: Section, taskDescription: string | null): boolean {
  if (!taskDescription) return false;

  const classification = requireUploadClassification(section);
  const baseMapping = BALANCE_HEALTH_MAPPINGS[classification];

  if (!baseMapping) return false;

  const instructions = getBalanceHealthUtahDocumentInstructions(baseMapping, section);
  const taskType = getAdditionalWorkflowTaskType(instructions.additionalWorkflow);

  if (!taskType) return false;

  return taskDescription.trim().toUpperCase().startsWith(`${taskType.toUpperCase()}:`);
}

// Region ops manager, keyed by fax line. Source of truth for the OTHER-classification
// assignees and the fallback used when a location sentinel can't resolve (per the ticket,
// "fallback logic is the ops manager"). From the Providers Mapping "Ops Manager" column.
export const BALANCE_HEALTH_OPS_MANAGER_BY_LINE: Partial<Record<BhFaxLine, string>> = {
  [BhFaxLine.UT]: "Thompson, Shawn",
  [BhFaxLine.SOCAL]: "Damian, Gloria",
  [BhFaxLine.FCC]: "Bradley, Felicia",
  [BhFaxLine.DESERT]: "Rivera, Danna",
  [BhFaxLine.AFAPS]: "Hosseinipour, Nelly",
  [BhFaxLine.ONE_F_TWO_F]: "Opoku, Kwame",
  [BhFaxLine.FASWM]: "Boznango, Julie",
  [BhFaxLine.UFAI]: "Deynes, Tabitha",
  [BhFaxLine.IFAC]: "Tinner, Denise",
  [BhFaxLine.PRECISION]: "Elizarraz, Yolanda",
  [BhFaxLine.NV]: "Purvis, Porschia",
  [BhFaxLine.FACR]: "Keep, Carolyn",
  [BhFaxLine.WFAI]: "Guerrero, Rosalba",
  [BhFaxLine.BAFC]: "Peralta, Maria",
  [BhFaxLine.SFAC]: "Alsbury, Elizabeth",
  [BhFaxLine.PPO]: "Alsbury, Elizabeth",
  [BhFaxLine.VPG]: "Alvarez, Elizabeth",
};

// Region front-desk queue, keyed by fax line. Stub: only the unprovisioned Precision line
// uses the "Location Front Desk" sentinel today, so this falls back to the ops manager.
// TODO: populate per-clinic front-desk names as lines are provisioned.
export const BALANCE_HEALTH_FRONT_DESK_BY_LINE: Partial<Record<BhFaxLine, string>> = {
  [BhFaxLine.BAFC]: "CA BAFC Front Desk",
  [BhFaxLine.SFAC]: "CA SFAC Front Desk",
  [BhFaxLine.PPO]: "CA PPO Front Desk",
  [BhFaxLine.VPG]: "CA VPG Front Desk",
};

// MA queue keyed by the patient's most-recent-encounter ModMed Location id (see balanceEncounterLocationStep).
// LOCATION_MA resolves via this table when the fax line is listed; unmapped/missing → region ops manager.
// Ids from harvest-balance-health-modmed-locations.ts. IFAC + FACR are provisioned; UFAI + Precision are
// not yet wired to a RingCentral number (BH_FAX_NUMBER_TO_LINE) so they stay inert until provisioned.
export const BALANCE_HEALTH_LOCATION_MA_BY_LINE: Partial<Record<BhFaxLine, Record<string, string>>> = {
  [BhFaxLine.IFAC]: {
    "5247": "IFACW Mount Vernon", // 311 East Division, Mount Vernon, WA 98274
    "5248": "IFACW Seattle",
    "5249": "IFACW Mercer Island/Renton", // Renton
    "5250": "IFACW Everett/Kirkland", // Everett
    "5251": "IFACW Mill Creek",
    "5261": "IFACW Everett/Kirkland", // Kirkland
  },
  [BhFaxLine.FACR]: {
    "5292": "CO Denver FD & MA",
    "5293": "CO Greeley FD & MA",
    "5294": "CO Longmont FD & MA",
    "5295": "CO Centennial FD & MA",
    "5297": "CO Avon FD & MA",
    "5296": "CO Frisco FD & MA",
  },
  [BhFaxLine.UFAI]: {
    "5099": "CA Levine San Pedro FD & MA", // San Pedro, Ste 150
    "3849": "CA Valencia-Cinema FD/MA",
    "4538": "CA Sherman Oaks FD & MA",
    "3912": "CA Santa Monica Medical Assistant",
    "3925": "CA Westlake Medical Assistant",
    "3918": "CA Santa Barbara Medical Assistant",
    "3924": "Valencia MA", // Valencia, Tourney
  },
  [BhFaxLine.PRECISION]: {
    "3847": "CA San Pedro Medical Assistant", // San Pedro, Suite 240
    "3846": "CA Torrance Medical Assistant",
  },
};

export const BALANCE_HEALTH_SOCAL_LOCATION_EHR_IDS = [
  "5340", // La Jolla
  "5350", // Chula Vista
  "5352", // Hemet
  "5351", // La Mesa
  "5341", // Encinitas
  "5342", // Escondido
  "5346", // Murrieta (Baxter)
  "5343", // Murrieta (Hot Springs)
  "5345", // Oceanside
  "5344", // San Diego
] as const;

export const BALANCE_HEALTH_WEIL_LOCATION_EHR_IDS = [
  "1451", // Bannockburn
  "1464", // Chicago Ridge
  "1459", // Avalon Park
  "4267", // Beverly
  "1447", // Lakeview
  "1443", // Ravenswood
  "5035", // Rush
  "1441", // Lincoln Park
  "4743", // Elgin
  "3766", // Elmwood Park
  "1448", // Glenview
  "1450", // Lemont
  "1442", // Libertyville
  "1433", // Mount Prospect
  "1434", // Niles
  "1444", // Norridge
  "1445", // Park Ridge
  "1446", // Skokie
  "1435", // Wilmette
  "4554", // Schaumburg
] as const;

/** SoCal Foot Care provider NPIs. */
export const BALANCE_HEALTH_SOCAL_PROVIDER_NPIS: readonly string[] = [
  "1831756105",
  "1932551082",
  "1235886037",
  "1942654959",
  "1750331252",
  "1811939176",
  "1629071725",
  "1750492955",
  "1396476628",
  "1609836543",
  "1013653385",
  "1396744959",
  "1548271828",
  "1548641566",
  "1811986599",
  "1093706590",
  "1023691995",
  "1831143452",
  "1235202722",
  "1699417774",
  "1881338317",
  "1235400060",
] as const;

/** Weil Foot & Ankle (WFAI) provider NPIs. */
export const BALANCE_HEALTH_WEIL_PROVIDER_NPIS: readonly string[] = [
  "1124468657",
  "1225025059",
  "1215935739",
  "1467468207",
  "1063090702",
  "1184795239",
  "1659379246",
  "1407296171",
  "1235227851",
  "1134860091",
  "1821096439",
  "1083612683",
  "1437166212",
  "1255838975",
  "1902269301",
  "1588804520",
  "1518383686",
  "1578093795",
  "1689961757",
  "1942203450",
  "1932111606",
  "1063142503",
  "1720341035",
  "1154784486",
  "1750491288",
  "1750818597",
  "1205369410",
  "1891788055",
  "1598762601",
  "1972919702",
  "1952475451",
  "1457694481",
  "1225092901",
  "1992905376",
  "1891177978",
  "1417410077",
  "1053794859",
  "1922085505",
  "1730421884",
  "1861061244",
  "1205577871",
  "1558718544",
  "1831777499",
  "1356874150",
  "1699354308",
  "1093034407",
  "1508210808",
  "1033190079",
  "1760066682",
  "1316563612",
  "1023016623",
  "1891375598",
  "1396447637",
  "1427322775",
  "1558718627",
  "1821096785",
  "1780971523",
  "1942208558",
] as const;

/** SoCal Foot Care (CA) scheduling fax line, used for Balance Health scheduling-region resolution. */
export const BALANCE_HEALTH_SOCAL_SCHEDULING_FAX_NUMBER = "+18586666839";

/** Weil Foot & Ankle (WFAI, IL/WI) scheduling fax line, used for Balance Health scheduling-region resolution. */
export const BALANCE_HEALTH_WEIL_SCHEDULING_FAX_NUMBER = "+18477379586";

export const BALANCE_HEALTH_SCHEDULING_TAG = "scheduling needed";

const BALANCE_HEALTH_SCHEDULING_TAG_FAX_LINES: readonly string[] = [
  BALANCE_HEALTH_SOCAL_FAX_LINE, // SoCal Foot Care (+18586666839)
  BALANCE_HEALTH_WFAI_FAX_LINE, // Weil Foot & Ankle (+18477379586)
];

export const BALANCE_HEALTH_MIN_SLOT_LEAD_HOURS = 4;

export function getBalanceHealthReferralTaskInitialTags(faxRecipientNumber: string | null): string[] {
  if (faxRecipientNumber && BALANCE_HEALTH_SCHEDULING_TAG_FAX_LINES.includes(faxRecipientNumber)) {
    return [BALANCE_HEALTH_SCHEDULING_TAG];
  }

  return [];
}

// Front-desk queue keyed by encounter Location id. Parallel to BALANCE_HEALTH_LOCATION_MA_BY_LINE;
// LOCATION_FRONT_DESK resolves here when present, else the per-line BALANCE_HEALTH_FRONT_DESK_BY_LINE.
export const BALANCE_HEALTH_LOCATION_FRONT_DESK_BY_LINE: Partial<Record<BhFaxLine, Record<string, string>>> = {
  [BhFaxLine.PRECISION]: {
    "3847": "CA San Pedro Front Desk", // San Pedro, Suite 240
    "3846": "CA Torrance Front Desk",
  },
};

/** True when the fax line resolves an MA/front-desk sentinel by encounter location (vs. provider NPI / per-line). */
export function balanceHealthLineHasLocationTable(faxLine: BhFaxLine): boolean {
  return (
    BALANCE_HEALTH_LOCATION_MA_BY_LINE[faxLine] !== undefined ||
    BALANCE_HEALTH_LOCATION_FRONT_DESK_BY_LINE[faxLine] !== undefined
  );
}

/** ModMed Location ids mapped to a queue for this fax line (used to prefer office encounters). */
export function getBalanceHealthLocationIds(faxLine: BhFaxLine): string[] {
  return [
    ...new Set([
      ...Object.keys(BALANCE_HEALTH_LOCATION_MA_BY_LINE[faxLine] ?? {}),
      ...Object.keys(BALANCE_HEALTH_LOCATION_FRONT_DESK_BY_LINE[faxLine] ?? {}),
    ]),
  ];
}

// Default fallback assignee used when the inbound fax line does not match any
// entry in BALANCE_HEALTH_FALLBACK_ASSIGNEE_BY_FAX_LINE below.
export const BALANCE_HEALTH_FALLBACK_ASSIGNEE_DEFAULT = "Kim, Seung";

// Fallback assignee passed as `assign_to_fallback` to CloudCruise when the
// primary `assign_to` cannot be resolved. Keyed by inbound fax line.
export const BALANCE_HEALTH_FALLBACK_ASSIGNEE_BY_FAX_LINE: Record<string, string> = {
  [BALANCE_HEALTH_UTAH_FAX_LINE]: "UT Call Center",
  [BALANCE_HEALTH_SOCAL_FAX_LINE]: "Kim, Seung",
  [BALANCE_HEALTH_ARTISAN_FAX_LINE]: "Hosseinipour, Nelly",
  [BALANCE_HEALTH_DESERT_FAX_LINE]: "Rivera, Danna",
  [BALANCE_HEALTH_IFAC_FAX_LINE]: "Tinner, Denise",
  [BALANCE_HEALTH_WFAI_FAX_LINE]: "Guerrero, Rosalba",
  [BALANCE_HEALTH_FAC_ROCKIES_FAX_LINE]: "Bethencourt, Maria",
  [BALANCE_HEALTH_1F2F_FAX_LINE]: "Opoku, Kwame",
  [BALANCE_HEALTH_FASWM_FAX_LINE]: "Boznango, Julie",
  [BALANCE_HEALTH_LVFAC_FAX_LINE]: "Purvis, Porschia",
  [BALANCE_HEALTH_UFAI_FAX_LINE]: "Deynes, Tabitha",
  [BALANCE_HEALTH_PRECISION_FAX_LINE]: "Elizarraz, Yolanda",
  // Defaulted to the region ops manager, matching the pattern for every other single-location
  // line added after UT/SoCal (AFAPS, Desert, 1F2F, FASWM, UFAI, Precision, WFAI, NV) — confirm
  // with ops if these 4 should diverge like IFAC/FACR do.
  [BALANCE_HEALTH_BAFC_FAX_LINE]: "Peralta, Maria",
  [BALANCE_HEALTH_SFAC_FAX_LINE]: "Alsbury, Elizabeth",
  [BALANCE_HEALTH_PPO_FAX_LINE]: "Alsbury, Elizabeth",
  [BALANCE_HEALTH_VPG_FAX_LINE]: "Alvarez, Elizabeth",
};

// Region medical-records queue, keyed by fax line. From the Providers Mapping
// "Medical Records" column (authoritative for these names).
export const BALANCE_HEALTH_MEDICAL_RECORDS_BY_LINE: Partial<Record<BhFaxLine, string>> = {
  [BhFaxLine.UT]: "Medical Records - UT",
  [BhFaxLine.SOCAL]: "Medical Records - CA SCFC",
  [BhFaxLine.AFAPS]: "Medical Records - Artisan",
  [BhFaxLine.DESERT]: "Medical Records - AZ",
  [BhFaxLine.IFAC]: "Medical Records - WA",
  [BhFaxLine.WFAI]: "Medical Records - IL/IN/WI",
  [BhFaxLine.FCC]: "Medical Records - MT/WY",
  [BhFaxLine.FACR]: "Medical Records - CO",
  [BhFaxLine.ONE_F_TWO_F]: "Medical Records - VA",
  [BhFaxLine.FASWM]: "Medical Records - MI",
  [BhFaxLine.NV]: "Medical Records - NV",
  [BhFaxLine.UFAI]: "Medical Records - CA UFAI",
  [BhFaxLine.PRECISION]: "Medical Records - CA UFAI",
  [BhFaxLine.BAFC]: "Medical Records - CA BAFC",
  [BhFaxLine.SFAC]: "Medical Records - CA SFAC",
  [BhFaxLine.PPO]: "Medical Records - CA PPO",
  [BhFaxLine.VPG]: "Medical Records - CA VPG",
};

const balanceHealthClassificationsForDocUpload: Classification[] = [
  Classification.ADMIN_CERTIFICATE_OF_MEDICAL_NECESSITY,
  Classification.ADMIN_DISABILITY_FORMS,
  Classification.ADMIN_FMLA_FORMS,
  Classification.ADMIN_PATIENT_QUESTIONNAIRE,
  Classification.ADMIN_TRANSITION_CARE,
  Classification.ADMIN_WORK_OR_SCHOOL_PHYSICAL_FORMS,
  Classification.CLEARANCE_REQUEST,
  Classification.CLEARANCE_REQUEST_RESPONSE,
  Classification.DME_ORDER,
  Classification.FINANCIAL,
  Classification.FINANCIAL_ELIGIBILITY_OF_BENEFITS,
  Classification.IDENTIFICATION_CARD,
  Classification.IMAGING_RESULTS,
  Classification.IMAGING_RESULTS_CT,
  Classification.IMAGING_RESULTS_MRI,
  Classification.IMAGING_RESULTS_ULTRASOUND,
  Classification.INSURANCE_CARD,
  Classification.INTAKE,
  Classification.JUNK,
  Classification.LAB_RESULTS,
  Classification.LAB_RESULTS_PATHOLOGY,
  Classification.MEDICAL_HISTORY_HOME_HEALTH,
  Classification.MEDICAL_RECORD_REQUEST,
  Classification.NOTIFICATION,
  Classification.NOTIFICATION_HOSPITAL_ADMIT,
  Classification.ORDER_LAB,
  Classification.ORDER_PROCEDURE,
  Classification.OTHER,
  Classification.PHARMACY_RELATED,
  Classification.PHARMACY_RELATED_CARE_CONSIDERATION,
  Classification.PHARMACY_RELATED_NON_ADHERENCE,
  Classification.PHARMACY_RELATED_REFILL,
  Classification.PRIOR_AUTHORIZATION_MEDICATION_REQUEST,
  Classification.PRIOR_AUTHORIZATION_MEDICATION_RESPONSE,
  Classification.PRIOR_AUTHORIZATION_PROCEDURE_REQUEST,
  Classification.PRIOR_AUTHORIZATION_PROCEDURE_RESPONSE,
  Classification.PROGRESS_NOTE,
  Classification.PROGRESS_NOTE_HOSPITAL_DISCHARGE_SUMMARY,
  Classification.PROGRESS_NOTE_OPERATIVE_REPORT,
  Classification.PROGRESS_NOTE_PRE_OPERATIVE,
  Classification.REFERRAL,
  Classification.REFERRAL_RESPONSE,
  Classification.REFERRAL_STATUS_REQUEST,
];

const BalanceHealthClassificationMap = {
  [ClassificationCategory.ADMIN]: [
    Classification.ADMIN_CERTIFICATE_OF_MEDICAL_NECESSITY,
    Classification.ADMIN_DISABILITY_FORMS,
    Classification.ADMIN_FMLA_FORMS,
    Classification.ADMIN_PATIENT_QUESTIONNAIRE,
    Classification.ADMIN_TRANSITION_CARE,
    Classification.ADMIN_WORK_OR_SCHOOL_PHYSICAL_FORMS,
  ] as const,
  [ClassificationCategory.CLEARANCE_REQUEST]: [
    Classification.CLEARANCE_REQUEST,
    Classification.CLEARANCE_REQUEST_RESPONSE,
  ] as const,
  [ClassificationCategory.DME_ORDER]: [Classification.DME_ORDER] as const,
  [ClassificationCategory.FINANCIAL]: [
    Classification.FINANCIAL,
    Classification.FINANCIAL_ELIGIBILITY_OF_BENEFITS,
  ] as const,
  [ClassificationCategory.IDENTIFICATION_CARD]: [Classification.IDENTIFICATION_CARD] as const,
  [ClassificationCategory.IMAGING_RESULTS]: [
    Classification.IMAGING_RESULTS,
    Classification.IMAGING_RESULTS_CT,
    Classification.IMAGING_RESULTS_MRI,
    Classification.IMAGING_RESULTS_ULTRASOUND,
  ] as const,
  [ClassificationCategory.INSURANCE_CARD]: [Classification.INSURANCE_CARD] as const,
  [ClassificationCategory.INTAKE]: [Classification.INTAKE] as const,
  [ClassificationCategory.JUNK]: [Classification.JUNK] as const,
  [ClassificationCategory.LAB_RESULTS]: [Classification.LAB_RESULTS, Classification.LAB_RESULTS_PATHOLOGY] as const,
  [ClassificationCategory.MEDICAL_HISTORY]: [Classification.MEDICAL_HISTORY_HOME_HEALTH] as const,
  [ClassificationCategory.MEDICAL_RECORD_REQUEST]: [Classification.MEDICAL_RECORD_REQUEST] as const,
  [ClassificationCategory.NOTIFICATION]: [
    Classification.NOTIFICATION,
    Classification.NOTIFICATION_HOSPITAL_ADMIT,
  ] as const,
  [ClassificationCategory.ORDER_LAB]: [Classification.ORDER_LAB, Classification.ORDER_PROCEDURE] as const,
  [ClassificationCategory.OTHER]: [Classification.OTHER] as const,
  [ClassificationCategory.PHARMACY_RELATED]: [
    Classification.PHARMACY_RELATED,
    Classification.PHARMACY_RELATED_CARE_CONSIDERATION,
    Classification.PHARMACY_RELATED_NON_ADHERENCE,
    Classification.PHARMACY_RELATED_REFILL,
  ] as const,
  [ClassificationCategory.PRIOR_AUTHORIZATION]: [
    Classification.PRIOR_AUTHORIZATION_MEDICATION_REQUEST,
    Classification.PRIOR_AUTHORIZATION_MEDICATION_RESPONSE,
    Classification.PRIOR_AUTHORIZATION_PROCEDURE_REQUEST,
    Classification.PRIOR_AUTHORIZATION_PROCEDURE_RESPONSE,
  ] as const,
  [ClassificationCategory.PROGRESS_NOTE]: [
    Classification.PROGRESS_NOTE,
    Classification.PROGRESS_NOTE_HOSPITAL_DISCHARGE_SUMMARY,
    Classification.PROGRESS_NOTE_OPERATIVE_REPORT,
    Classification.PROGRESS_NOTE_PRE_OPERATIVE,
  ] as const,
  [ClassificationCategory.REFERRAL]: [Classification.REFERRAL, Classification.REFERRAL_RESPONSE] as const,
  [ClassificationCategory.REFERRAL_STATUS_REQUEST]: [Classification.REFERRAL_STATUS_REQUEST] as const,
} as const;

export async function getBalanceHealthWorkflows(
  _group: Group
): Promise<Partial<Record<WorkflowType, Classification[]>>> {
  return {
    [Workflow.REFERRAL]: BALANCE_HEALTH_REFERRAL_CLASSIFICATIONS,
    [Workflow.DOC_MANAGEMENT]: balanceHealthClassificationsForDocUpload.filter(
      (c) => !BALANCE_HEALTH_REFERRAL_CLASSIFICATIONS.includes(c)
    ),
  };
}

export function getBalanceHealthClassificationToExtract(): Classification[] {
  return balanceHealthClassificationsForDocUpload.filter((c) => c !== Classification.JUNK);
}

export function getBalanceHealthClassificationForDocumentUpload(): Classification[] {
  return balanceHealthClassificationsForDocUpload;
}

// Patientless classifications and multiple-patient sections route to a chartless general task.
const BALANCE_HEALTH_PATIENTLESS_CLASSIFICATIONS: ReadonlySet<Classification> = new Set([
  Classification.FINANCIAL,
  Classification.OTHER,
]);

export function isBalanceHealthPatientlessDoc(section: Section): boolean {
  return (
    section.hasMultiplePatients ||
    BALANCE_HEALTH_PATIENTLESS_CLASSIFICATIONS.has(section.uploadClassification || section.classification)
  );
}

export function getBalanceHealthClassificationsToDisplayForSelection(): ClassificationSection[] {
  return (Object.keys(BalanceHealthClassificationMap) as ClassificationCategory[]).map((category) => {
    const options = BalanceHealthClassificationMap[category as keyof typeof BalanceHealthClassificationMap];

    return {
      label: category,
      options: options.map((option) => option.toLowerCase()),
    };
  });
}

const BALANCE_HEALTH_SUPPLEMENTARY_CLASSIFICATIONS = new Set<Classification>([
  Classification.FINANCIAL,
  Classification.FINANCIAL_ELIGIBILITY_OF_BENEFITS,
  Classification.IDENTIFICATION_CARD,
  Classification.INSURANCE_CARD,
  Classification.INTAKE,
  Classification.JUNK,
  Classification.MEDICAL_RECORD_REQUEST,
  Classification.NOTIFICATION,
  Classification.NOTIFICATION_HOSPITAL_ADMIT,
  Classification.OTHER,
]);

const BALANCE_HEALTH_IMAGING_SET = new Set<Classification>([
  Classification.IMAGING_RESULTS,
  Classification.IMAGING_RESULTS_CT,
  Classification.IMAGING_RESULTS_MRI,
  Classification.IMAGING_RESULTS_ULTRASOUND,
]);

const BALANCE_HEALTH_LAB_SET = new Set<Classification>([
  Classification.LAB_RESULTS,
  Classification.LAB_RESULTS_PATHOLOGY,
]);

const BALANCE_HEALTH_PHARMACY_SET = new Set<Classification>([
  Classification.PHARMACY_RELATED,
  Classification.PHARMACY_RELATED_CARE_CONSIDERATION,
  Classification.PHARMACY_RELATED_NON_ADHERENCE,
  Classification.PHARMACY_RELATED_REFILL,
]);

const BALANCE_HEALTH_PROGRESS_NOTE_SET = new Set<Classification>([
  Classification.PROGRESS_NOTE,
  Classification.PROGRESS_NOTE_HOSPITAL_DISCHARGE_SUMMARY,
  Classification.PROGRESS_NOTE_OPERATIVE_REPORT,
  Classification.PROGRESS_NOTE_PRE_OPERATIVE,
]);

const BALANCE_HEALTH_DME_SET = new Set<Classification>([
  Classification.DME_ORDER,
  Classification.DME_DIABETIC_SUPPLIES,
  Classification.DME_REQUEST,
  Classification.DME_RESPONSE,
]);

export function getBalanceHealthGroupingClassification(classifications: Classification[]): Classification | null {
  const hasMedicalRecordRequest = classifications.includes(Classification.MEDICAL_RECORD_REQUEST);
  const substantive = classifications.filter((c) => !BALANCE_HEALTH_SUPPLEMENTARY_CLASSIFICATIONS.has(c));

  if (new Set(substantive).size !== substantive.length) {
    return null;
  }

  if (substantive.length === 0) {
    return hasMedicalRecordRequest ? Classification.MEDICAL_RECORD_REQUEST : null;
  }

  if (substantive.length === 1) {
    return substantive[0];
  }

  const HIERARCHY: Classification[] = [
    Classification.REFERRAL,
    Classification.PRIOR_AUTHORIZATION_MEDICATION_RESPONSE,
    Classification.PRIOR_AUTHORIZATION_PROCEDURE_RESPONSE,
    Classification.PRIOR_AUTHORIZATION_MEDICATION_REQUEST,
    Classification.PRIOR_AUTHORIZATION_PROCEDURE_REQUEST,
    Classification.CLEARANCE_REQUEST_RESPONSE,
    Classification.CLEARANCE_REQUEST,
  ];

  const topMatch = HIERARCHY.find((c) => substantive.includes(c));

  if (topMatch) {
    return topMatch;
  }

  const dmeMatch = substantive.find((c) => BALANCE_HEALTH_DME_SET.has(c));

  if (dmeMatch) {
    return dmeMatch;
  }

  if (substantive.every((c) => BALANCE_HEALTH_IMAGING_SET.has(c))) {
    return Classification.IMAGING_RESULTS;
  }

  if (substantive.every((c) => BALANCE_HEALTH_LAB_SET.has(c))) {
    return Classification.LAB_RESULTS;
  }

  if (substantive.every((c) => BALANCE_HEALTH_PHARMACY_SET.has(c))) {
    return Classification.PHARMACY_RELATED;
  }

  if (substantive.every((c) => BALANCE_HEALTH_PROGRESS_NOTE_SET.has(c))) {
    return Classification.PROGRESS_NOTE;
  }

  return null;
}

export function getBalanceHealthDocRoutingStrategy(
  section: Section,
  patient: Patient,
  faxRecipientNumber: string | null = null
): DocRoutingStrategy[] {
  if (faxRecipientNumber === BALANCE_HEALTH_SOCAL_FAX_LINE) {
    return getBalanceHealthSoCalDocRoutingStrategy(section, patient);
  }

  return getBalanceHealthUtahDocRoutingStrategy(section, patient);
}

function getBalanceHealthUtahDocRoutingStrategy(section: Section, patient: Patient): DocRoutingStrategy[] {
  if (section.classification === Classification.JUNK) {
    return [];
  }

  if (isPatientNotInEhr(patient)) {
    if (section.classification === Classification.ADMIN_FMLA_FORMS) {
      return [DocRoutingStrategy.EMAIL];
    }

    const providerNpi = getReceivingProviderNpi(section);

    if (providerNpi) {
      return [DocRoutingStrategy.EHR];
    }

    return [DocRoutingStrategy.FAX];
  }

  if (
    section.classification === Classification.OTHER &&
    getDocumentType(section)?.toLowerCase() === "employment verification"
  ) {
    return [DocRoutingStrategy.EMAIL];
  }

  return [DocRoutingStrategy.EHR];
}

function getBalanceHealthSoCalDocRoutingStrategy(section: Section, patient: Patient): DocRoutingStrategy[] {
  return getBalanceHealthUtahDocRoutingStrategy(section, patient);
}

export function getBalanceHealthDocRoutingEmailRecipients(
  section: Section,
  _patient: Patient | null,
  faxRecipientNumber: string | null = null
): string[] {
  const hrEmail =
    faxRecipientNumber === BALANCE_HEALTH_SOCAL_FAX_LINE ? BALANCE_HEALTH_SOCAL_HR_EMAIL : BALANCE_HEALTH_HR_EMAIL;

  if (section.classification === Classification.ADMIN_FMLA_FORMS) {
    return [hrEmail];
  }

  if (
    section.classification === Classification.OTHER &&
    getDocumentType(section)?.toLowerCase() === "employment verification"
  ) {
    return [hrEmail];
  }

  return [];
}

export async function getBalanceHealthDocumentInstructions(
  baseMapping: DocumentMapping,
  _task: Task,
  _group: Group,
  section: Section,
  faxRecipientNumber: string | null = null,
  stepOutputs?: StepOutputs
): Promise<DynamicDocumentInstructions> {
  const reviewedContent = (section.reviewedContent ?? {}) as Record<string, unknown>;
  // Per-classification logic owns folder / label / task type (and the signature workflow).
  const base = getBalanceHealthUtahDocumentInstructions(baseMapping, section);
  const faxLine = getBhFaxLine(faxRecipientNumber);

  // Signature-required docs always route to the receiving provider; the per-fax-line
  // assignment table does not apply (you can't skip a signature).
  if (getBooleanField(reviewedContent, "signatureRequired")) {
    return resolveAssignedPhysician(base, section, faxLine);
  }

  const encounterLocationId = stepOutputs?.balance_encounter_location?.encounterLocationId ?? null;

  return applyBalanceHealthFaxLineAssignment(base, section, faxRecipientNumber, encounterLocationId);
}

// Resolves the generic "Physician" placeholder to the actual extracted provider — by NPI first,
// then by name match — falling back to the region ops manager for this fax line, and finally to
// the generic literal if even that has no entry.
function resolvePhysicianName(section: Section, faxLine: BhFaxLine): string {
  return (
    getBalanceHealthPhysicianAssigneeByNpi(section) ??
    getBalanceHealthPhysicianAssigneeByName(section) ??
    BALANCE_HEALTH_OPS_MANAGER_BY_LINE[faxLine] ??
    ASSIGN_TO_PHYSICIAN
  );
}

function resolveAssignedPhysician(
  instructions: DynamicDocumentInstructions,
  section: Section,
  faxLine: BhFaxLine
): DynamicDocumentInstructions {
  return instructions.assignedTo === ASSIGN_TO_PHYSICIAN
    ? { ...instructions, assignedTo: resolvePhysicianName(section, faxLine) }
    : instructions;
}

/**
 * Overlays the per-fax-line assignee from the routing table onto the base instructions.
 * When the inbound fax line has no assignee for this classification/condition, the task is
 * dropped (assignedTo = null, additionalWorkflow = null) so no ModMed task / CloudCruise run
 * is created — the document is still uploaded. See balanceTaskSkipCheckStep.
 */
function applyBalanceHealthFaxLineAssignment(
  base: DynamicDocumentInstructions,
  section: Section,
  faxRecipientNumber: string | null,
  encounterLocationId: string | null
): DynamicDocumentInstructions {
  const classification = section.uploadClassification || section.classification;
  const faxLine = getBhFaxLine(faxRecipientNumber);
  const fields = getRoutingFieldsFromReviewedContent((section.reviewedContent ?? {}) as Record<string, unknown>);
  const resolved = resolveBalanceHealthRouting(classification, faxLine, fields);

  // No routing entry for this classification → keep the base instructions unchanged.
  if (!resolved) return resolveAssignedPhysician(base, section, faxLine);

  const assignedTo = resolveBhAssignee(resolved.rawAssignee, section, faxLine, encounterLocationId);

  // Empty cell for this fax line → no task, no CloudCruise (document still uploaded).
  if (assignedTo === null) return { ...base, assignedTo: null, additionalWorkflow: null };

  return { ...base, assignedTo };
}

/** Resolve a routing-table assignee sentinel to a concrete ModMed assignee for this fax line. */
function resolveBhAssignee(
  rawAssignee: string | null,
  section: Section,
  faxLine: BhFaxLine,
  encounterLocationId: string | null
): string | null {
  if (!rawAssignee) return null;

  switch (rawAssignee) {
    case ASSIGNEE.PHYSICIAN:
      return resolvePhysicianName(section, faxLine);
    case ASSIGNEE.LOCATION_MA:
      return resolveLocationMa(section, faxLine, encounterLocationId);
    case ASSIGNEE.LOCATION_FRONT_DESK:
      return resolveLocationFrontDesk(faxLine, encounterLocationId);
    case ASSIGNEE.LOCATION_MEDICAL_RECORDS:
      return BALANCE_HEALTH_MEDICAL_RECORDS_BY_LINE[faxLine] ?? null;
    case ASSIGNEE.LOCATION_OPS_MANAGER:
      return BALANCE_HEALTH_OPS_MANAGER_BY_LINE[faxLine] ?? null;
    default:
      return rawAssignee;
  }
}

// True when the resolved mapping's task type is "Sign" — covers both ops-set
// signatureRequired and always-Sign classifications (DME certs, LMNs, orders).
export function isBalanceHealthSignatureTask(section: Section): boolean {
  const classification = section.uploadClassification || section.classification;
  const baseMapping = BALANCE_HEALTH_MAPPINGS[classification];

  if (!baseMapping) return false;

  const instructions = getBalanceHealthUtahDocumentInstructions(baseMapping, section);

  return getAdditionalWorkflowTaskType(instructions.additionalWorkflow) === "Sign";
}

function getAdditionalWorkflowTaskType(additionalWorkflow: readonly string[] | null | undefined): string | null {
  for (const line of additionalWorkflow ?? []) {
    const [rawKey, ...rest] = line.split(":");

    if (rawKey?.trim().toLowerCase() === "task type") return rest.join(":").trim();
  }

  return null;
}

export const BALANCE_HEALTH_PROVIDERS_BY_NPI: Readonly<Record<string, { physician: string; ma: string | null }>> = {
  "1023390531": { physician: "Church, Robert, DPM", ma: "Gren, Shaelee" },
  "1700865748": { physician: "Clark, Scott, DPM", ma: "Brown, Janell" },
  "1588304042": { physician: "Buch, Saloni, DPM", ma: "Kahanani, Maha" },
  "1508151341": { physician: "McPhie, Jared, DPM", ma: "McDonald, Taiza" },
  "1730707829": { physician: "Seegmiller, Alex, DPM", ma: null },
  "1174539449": { physician: "Seegmiller, Richard, DPM", ma: "Jackson, Tiffany" },
  "1396230363": { physician: "Veater, Raddison, DPM", ma: "Hovis, Samantha" },
  "1902328644": { physician: "Packard, Weston, DPM", ma: null },
  "1659683381": { physician: "Goebel, Christiaan, DPM", ma: null },
  "1639306889": { physician: "Preece, Dan, DPM", ma: null },
  "1396972469": { physician: "Groberg, Darren, DPM", ma: null },
  "1134125511": { physician: "Smith, Christian, DPM", ma: null },
  "1710114962": { physician: "Shelton, Scott, DPM", ma: null },
  "1831756105": { physician: "Berger, Colby, DPM", ma: "Pumariega, Mary" },
  "1932551082": { physician: "Kreps, Christopher, DPM", ma: "Ortiz, Jessica" },
  "1235886037": { physician: "Ibrahim, Fawzy, DPM", ma: "Anderson, Angelina" },
  "1942654959": { physician: "Wong, Amy, DPM", ma: "Santiago, Mya" },
  "1750331252": { physician: "Vallone, Robert, DPM", ma: "Ceriteno, Karen" },
  "1811939176": { physician: "Yeager-Smith, Lora", ma: "Spendler, Brianna" },
  "1629071725": { physician: "Meyer, Joan, DPM", ma: "Beltran, Ashley" },
  "1750492955": { physician: "Evans, Richard, DPM", ma: "Cervantes, Marlene" },
  "1396476628": { physician: "Collins, Brodie, DPM", ma: "Gastelum, Keilah" },
  "1609836543": { physician: "Drake, Robert, DPM", ma: "Orozco, Maria" },
  "1013653385": { physician: "Nguyen, Ahn-Thy, DPM", ma: "Ortigoza, Casandra" },
  "1396744959": { physician: "Tucker, Daniel, DPM", ma: "Andres, Abigail" },
  "1548271828": { physician: "Santoro, John, DPM", ma: "Hernandez Ayala, Fabiola" },
  "1548641566": { physician: "Deliman, Jordan, DPM", ma: "Gonzalez, Vicky" },
  "1811986599": { physician: "Lieberman, Ronald, DPM", ma: "Estrada, Daniela" },
  "1093706590": { physician: "Ruetenik, Brad, DPM", ma: "Wong, Haley" },
  "1023691995": { physician: "Ley, Gabriella, DPM", ma: "Duxbury, Caitlyn" },
  "1831143452": { physician: "Mazza, David, DPM", ma: "Rios, Ayme" },
  "1235202722": { physician: "Anderson, Karen, DPM", ma: "Colbath, Julianne" },
  "1699417774": { physician: "Bustos, Lindsey, DPM", ma: "Lira, Jessica" },
  "1881338317": { physician: "Daneshvar, Yasamin, DPM", ma: "Simpson, Javon" },
  "1972563955": { physician: "Harrill, Jason, DPM", ma: null },
  "1780643395": { physician: "Maling, Scott, DPM", ma: null },
  "1932356805": { physician: "Pipher, Mark, DPM", ma: null },
  "1487386546": { physician: "Strothman, Samuel, DPM", ma: null },
  "1699304741": { physician: "Ross, Amy, DPM", ma: null },
  "1083216949": { physician: "Wallace, Blake, DPM", ma: null },
  "1235171067": { physician: "Glazer, Devon, DPM", ma: null },
  "1275567141": { physician: "Goddard, Michael, DPM", ma: null },
  "1700267960": { physician: "Golshahi, Bahar, DPM", ma: null },
  "1194245795": { physician: "Hehe, Kyle, DPM", ma: null },
  "1437870839": { physician: "Heng, Daliya, DPM", ma: null },
  "1659672590": { physician: "Meyers, Samuel, DPM", ma: null },
  "1164795209": { physician: "Nosrati, Sam, DPM", ma: null },
  "1932761822": { physician: "Schmalhaus, Monte, DPM", ma: null },
  "1124468657": { physician: "Alianello, Nicholas, DPM", ma: null },
  "1225025059": { physician: "Arain-Saleem, Saera, DPM", ma: null },
  "1215935739": { physician: "Baker, Jeffrey, DPM", ma: null },
  "1467468207": { physician: "Beaver, Bart, DPM", ma: null },
  "1063090702": { physician: "Beci, Nicolas, DPM", ma: null },
  "1184795239": { physician: "Bender, Mary Ann, DPM", ma: null },
  "1659379246": { physician: "Benton-Weil, Wendy, DPM", ma: null },
  "1407296171": { physician: "Bernstein, Jennifer, DPM", ma: null },
  "1235227851": { physician: "Bialon, Beata, DPM", ma: null },
  "1134860091": { physician: "Bond, Regan, DPM", ma: null },
  "1821096439": { physician: "Bongiovanni, Frank, DPM", ma: null },
  "1083612683": { physician: "Borrelli, Anthony, DPM", ma: null },
  "1437166212": { physician: "Buck, Michael, DPM", ma: null },
  "1255838975": { physician: "Burdi, Robert Drew, DPM", ma: null },
  "1902269301": { physician: "Cabai, Jillian, DPM", ma: null },
  "1588804520": { physician: "Christopoulos, Ioulia", ma: null },
  "1518383686": { physician: "Dabul, Monica", ma: null },
  "1578093795": { physician: "Denden, Sharif, DPM", ma: null },
  "1689961757": { physician: "Doyle, Lauren, DPM", ma: null },
  "1942203450": { physician: "Finn, William, DPM", ma: null },
  "1932111606": { physician: "Fleischer, Adam, DPM", ma: null },
  "1063142503": { physician: "Gahr, Kamden, DPM", ma: null },
  "1720341035": { physician: "Garbula, Anna, DPM", ma: null },
  "1154784486": { physician: "George, Nathaniel, DPM", ma: null },
  "1750491288": { physician: "Gerst, David, DPM", ma: null },
  "1750818597": { physician: "Gray, Stephanie, DPM", ma: null },
  "1205369410": { physician: "Grigoropoulos, Katerina, DPM", ma: null },
  "1891788055": { physician: "Halinski, Catherine, DPM", ma: null },
  "1598762601": { physician: "Hersh, Bryan, DPM", ma: null },
  "1972919702": { physician: "Hook, Jonathan, DPM", ma: null },
  "1952475451": { physician: "Hugar, Ronald, DPM", ma: null },
  "1457694481": { physician: "Jacoby, Cory, DPM", ma: null },
  "1225092901": { physician: "Kaleta, Jennifer, DPM", ma: null },
  "1992905376": { physician: "Kim, Joshua, DPM", ma: null },
  "1891177978": { physician: "Kratzmeyer, Ridhi, DPM", ma: null },
  "1417410077": { physician: "Lawson, Alexander, DPM", ma: null },
  "1053794859": { physician: "Malik, Ali, DPM", ma: null },
  "1922085505": { physician: "McNeill, Misty, DPM", ma: null },
  "1730421884": { physician: "Park, Hannah, DPM", ma: null },
  "1861061244": { physician: "Patel, Kayna, DPM", ma: null },
  "1205577871": { physician: "Perry, Donovan, DPM", ma: null },
  "1558718544": { physician: "Polintan, Dencel", ma: null },
  "1831777499": { physician: "Sadi, Nader, DPM", ma: null },
  "1356874150": { physician: "Sakla, John, DPM", ma: null },
  "1699354308": { physician: "Santiago, Hector, DPM", ma: null },
  "1093034407": { physician: "Siddiqui, Bilal, DPM", ma: null },
  "1508210808": { physician: "Siddiqui, Jehanzab, DPM", ma: null },
  "1033190079": { physician: "Smith, Carlos, DPM", ma: null },
  "1760066682": { physician: "Syed, Ilham, DPM", ma: null },
  "1316563612": { physician: "Tilsley, Jacob", ma: null },
  "1023016623": { physician: "Weil Jr, Lowell, DPM", ma: null },
  "1942208558": { physician: "Weil Jr, Lowell, DPM", ma: null },
  "1639459068": { physician: "Kane, Jason, DPM", ma: null },
  "1295744647": { physician: "McLaws, Craig, DPM", ma: null },
  "1568482115": { physician: "Wilkinson, Stephen, DPM", ma: null },
  "1891957023": { physician: "Atherton, Stacy, DPM", ma: null },
  "1841810017": { physician: "Coté, Joseph, DPM", ma: null },
  "1104818061": { physician: "Jaakola, Eric, DPM", ma: null },
  "1649554882": { physician: "Jovic, Olivera, DPM", ma: null },
  "1972063519": { physician: "Kavanagh, Amber, DPM", ma: null },
  "1912093071": { physician: "Maurer, Brian, DPM", ma: null },
  "1184206708": { physician: "Mayo, Hannah, DPM", ma: null },
  "1306322201": { physician: "Seybold, Erica, DPM", ma: null },
  "1255336400": { physician: "Thelander, Erik, DPM", ma: null },
  "1265429369": { physician: "Vaardahl, Michael, DPM", ma: null },
  "1174599302": { physician: "Weber, Anna, DPM", ma: null },
  "1891375598": { physician: "Abbas, Shuja, DPM", ma: null },
  "1063897916": { physician: "Cullen, Yan-Rong, DPM", ma: null },
  "1568083806": { physician: "Garcia, Michelle, DPM", ma: null },
  "1396150652": { physician: "Goff, Jana, DPM", ma: null },
  "1790885358": { physician: "Hale, Douglas, DPM", ma: null },
  "1669872925": { physician: "Hansen, Nathan, DPM", ma: null },
  "1831237312": { physician: "Huppin, Lawrence, DPM", ma: null },
  "1104266030": { physician: "Malin, Paris, DPM", ma: null },
  "1134569734": { physician: "Reiner, Matthew, DPM", ma: null },
  "1225438328": { physician: "Tirabassi, Nathan, DPM", ma: null },
  "1982061180": { physician: "Wireman, Garrett, DPM", ma: null },
  "1235400060": { physician: "Kaplan, Harris, DPM", ma: null },
  "1396447637": { physician: "Rifai, Amer, DPM", ma: null },
  // 1Foot 2Foot Centre for Foot & Ankle Care
  "1154416352": { physician: "Barbella, Marc, DPM", ma: null },
  "1972246114": { physician: "Berberich, Erich, DPM", ma: null },
  "1124401948": { physician: "Carroll, Devin, DPM", ma: null },
  "1437692530": { physician: "Costella, Gerard, DPM", ma: null },
  "1144226853": { physician: "Dairman, Matthew, DPM", ma: null },
  "1386205839": { physician: "Kim, Joshua, DPM", ma: null },
  "1750744355": { physician: "Misseri, Nunzio, DPM", ma: null },
  "1275875718": { physician: "Moffett, Eric, DPM", ma: null },
  "1346987948": { physician: "Moradia, Bhakti, DPM", ma: null },
  "1770145856": { physician: "Shah, Ashmi, DPM", ma: null },
  // Foot & Ankle Specialists of West Michigan, PLLC
  "1447281662": { physician: "Bergsma, Corwyn, DPM", ma: null },
  "1619388857": { physician: "Brown, Andrew, DPM", ma: null },
  "1881113496": { physician: "Cleypool, Katelyn, DPM", ma: null },
  "1912318502": { physician: "David, Brianna, DPM", ma: null },
  "1023053436": { physician: "Hulst, Timothy, DPM", ma: null },
  "1730749961": { physician: "Kotzeva, Krista, DPM", ma: null },
  "1679501290": { physician: "Meyer, Patrick, DPM", ma: null },
  "1326084013": { physician: "Mossel, Jeff, DPM", ma: null },
  "1588907067": { physician: "Sundling, Rebecca, DPM", ma: null },
  "1609506740": { physician: "Treimer, Joshua, DPM", ma: null },
  "1063435659": { physician: "Uebele, Stacy, DPM", ma: null },
  // University Foot and Ankle Institute
  "1952310799": { physician: "Baravarian, Babak, DPM", ma: null },
  "1215946157": { physician: "Franson, Justin, DPM", ma: null },
  "1922568369": { physician: "Grecea, Bogdan", ma: null },
  "1659857654": { physician: "Johnson, Abimbola, DPM", ma: null },
  "1215518105": { physician: "Korrapati, Akhil, DPM", ma: null },
  "1134708100": { physician: "Kwon, Gi Ryoung, DPM", ma: null },
  "1720010200": { physician: "Lavian, Dana, DPM", ma: null },
  "1437379583": { physician: "Levine, Bruce, DPM", ma: null },
  "1699700518": { physician: "Levy, Leslie, DPM", ma: null },
  "1417518374": { physician: "Naei, Hoda, DPM", ma: null },
  "1982348280": { physician: "Olivova, Jana, DPM", ma: null },
  "1790309672": { physician: "Porciello-Donnarumma, Mario, DPM", ma: null },
  "1669081709": { physician: "Yoakim, Michelle, DPM", ma: null },
  // CA Precision Surgery Center
  "1710509336": { physician: "Barari, Omead, DPM", ma: null },
  "1366534216": { physician: "Feit, Eric, DPM", ma: null },
  "1104998434": { physician: "Kashanian, Alona, DPM", ma: null },
  // NV Las Vegas - Flamingo
  "1487684189": { physician: "Kelly, Pardis, DPM", ma: null },
  "1427006592": { physician: "Loveland, Lacey, DPM", ma: null },
  "1083725436": { physician: "Aramini, Michael, DPM", ma: null },
  "1942292883": { physician: "Torgesen, Gerald, DPM", ma: null },
  "1053722702": { physician: "McArthur, Troy, DPM", ma: null },
  "1245222181": { physician: "Larsen, Philip, DPM", ma: null },
  "1558691071": { physician: "Larkin, Kirk, DPM", ma: null },
  "1194068767": { physician: "Gardner, Richard, DPM", ma: null },
  "1548252224": { physician: "Reed, Roger, DPM", ma: null },
  "1689802332": { physician: "Tilton, Jeremy, DPM", ma: null },
  "1902331705": { physician: "Lisonbee, Joshua, DPM", ma: null },
  // WA IFAC Everett
  "1407827454": { physician: "Anderson, Randolph, DPM", ma: null },
  "1265094700": { physician: "Chang, Nicholas, DPM", ma: null },
  // CA BAFC Alameda
  "1780252213": { physician: "Boga, Nikhil, DPM", ma: null },
  "1508314808": { physician: "Abraham, Jonathan, DPM", ma: null },
  "1669051116": { physician: "Ahmed, Palwasha, DPM", ma: null },
  "1568991073": { physician: "Albright, Joe, DPM", ma: null },
  "1487143640": { physician: "Albright, Kristen, DPM", ma: null },
  "1467973230": { physician: "Alvarez, Audrey, DPM", ma: null },
  "1467835991": { physician: "An, Stephen, DPM", ma: null },
  "1063776466": { physician: "Balliet, Megan, DPM", ma: null },
  "1033266721": { physician: "Battula, Sireesha, DPM", ma: null },
  "1215940283": { physician: "Breuner, Lisa, DPM", ma: null },
  "1508386038": { physician: "Bridges, Christina, DPM", ma: null },
  "1255795050": { physician: "Coast, Reed, DPM", ma: null },
  "1457996753": { physician: "Corley, Kristina, DPM", ma: null },
  "1215020730": { physician: "Cornelison, Michael, DPM", ma: null },
  "1104433226": { physician: "Cummins, Michelle, DPM", ma: null },
  "1609307180": { physician: "Dhatt, Navdeep, DPM", ma: null },
  "1891097655": { physician: "Dini, Monara, DPM", ma: null },
  "1265012348": { physician: "Dyquiangco, Monique, DPM", ma: null },
  "1023112554": { physician: "Elardo, Thomas, DPM", ma: null },
  "1619996105": { physician: "Erickson, Sonia, DPM", ma: null },
  "1578121927": { physician: "Gill, Sharanbir, DPM", ma: null },
  "1609967413": { physician: "Gregori, Jeffery, DPM", ma: null },
  "1144071093": { physician: "Handa, Aaron, DPM", ma: null },
  "1841788320": { physician: "Harrison, Lisa, DPM", ma: null },
  "1760580310": { physician: "Hiatt, Jason, DPM", ma: null },
  "1023011459": { physician: "Hoffman, Anthony, DPM", ma: null },
  "1629564869": { physician: "Hom, Megan, DPM", ma: null },
  "1679712624": { physician: "Howard, Adam, DPM", ma: null },
  "1851828594": { physician: "Hurless, Michelle, DPM", ma: null },
  "1962595611": { physician: "Hurray, Alvie, DPM", ma: null },
  "1942731021": { physician: "Ishizuka, Toby, DPM", ma: null },
  "1124641436": { physician: "Jaswal, Jessica, DPM", ma: null },
  "1063729630": { physician: "Javed, Najwa, DPM", ma: null },
  "1952133621": { physician: "Jordan, Thomas, DPM", ma: null },
  "1962077693": { physician: "Kennedy, Matthew, DPM", ma: null },
  "1558425595": { physician: "Khoury, Christine, DPM", ma: null },
  "1538232731": { physician: "Kreger, Jonathan, DPM", ma: null },
  "1619320769": { physician: "Krepkiy, Viktoriya, DPM", ma: null },
  "1598573289": { physician: "Lam, Jenny, DPM", ma: null },
  "1518996446": { physician: "Laver, David, DPM", ma: null },
  "1992049571": { physician: "Lee, Jasper, DPM", ma: null },
  "1982187027": { physician: "Lee, Jisun, DPM", ma: null },
  "1619751450": { physician: "Potter, Jessica, DPM", ma: null },
  "1508665316": { physician: "Tran, David, DPM", ma: null },
  "1801117874": { physician: "Vartivarian, Mher, DPM", ma: null },
  "1528053535": { physician: "Vlahovic, Tracey, DPM", ma: null },
  "1730167370": { physician: "Weiner, Paul, DPM", ma: null },
  "1174189369": { physician: "Weyandt, Zachery, DPM", ma: null },
  "1598210510": { physician: "Woo, Renee, DPM", ma: null },
  "1134706625": { physician: "Wood, Meaghan, DPM", ma: null },
  "1952438566": { physician: "Yu, Jenny, DPM", ma: null },
  // CA BAFC Davis (SFAC)
  "1053602540": { physician: "Bills, Adam, DPM", ma: null },
  "1053727529": { physician: "Cho, Eunice, DPM", ma: null },
  "1235511627": { physician: "Haynam, Stephen, DPM", ma: null },
  "1902898059": { physician: "Kahan, David, DPM", ma: null },
  "1568807212": { physician: "Kim, Byunghee, DPM", ma: null },
  "1205907227": { physician: "Kirby, Kevin, DPM", ma: null },
  "1730748401": { physician: "Koga, Eric, DPM", ma: null },
  "1780067066": { physician: "Monson, Marshall, DPM", ma: null },
  "1912468497": { physician: "Nguyen, Julian, DPM", ma: null },
  "1548838972": { physician: "Parkman, Liliya, DPM", ma: null },
  "1376568774": { physician: "Silverio, George, DPM", ma: null },
  "1669009791": { physician: "Theis, Dustin, DPM", ma: null },
  "1043589211": { physician: "Tovmassian, Gregory, DPM", ma: null },
  "1356631048": { physician: "Wells, David, DPM", ma: null },
  "1942321138": { physician: "Wilkes, Craig, DPM", ma: null },
  // CA BAFC Folsom (PPO)
  "1023545407": { physician: "Bottini, Brandon, DPM", ma: null },
  "1033642335": { physician: "Eller, Lauren, DPM", ma: null },
  "1740785021": { physician: "Galli, Christopher, DPM", ma: null },
  "1841925039": { physician: "Garcia, Morgan, DPM", ma: null },
  "1518523489": { physician: "Lipari, Melissa, DPM", ma: null },
  "1558654921": { physician: "Ripp, Gavin, DPM", ma: null },
  // CA BAFC Modesto (VPG)
  "1962145359": { physician: "Hang, Sambo, DPM", ma: null },
  "1568660439": { physician: "Kaur, Amrita, DPM", ma: null },
  "1053429027": { physician: "Shimozaki, Kenneth, DPM", ma: null },
  "1538787395": { physician: "Singh, Rajan, DPM", ma: null },
  "1356710974": { physician: "Stauffer, Joshua, DPM", ma: null },
  "1730568163": { physician: "Telmo, Mariel, DPM", ma: null },
};

// Canonical roster assignee for an NPI (null when the NPI is absent or not in the roster).
export function getBalanceHealthPhysicianAssigneeForNpi(npi: string | null | undefined): string | null {
  return npi ? (BALANCE_HEALTH_PROVIDERS_BY_NPI[npi]?.physician ?? null) : null;
}

export function getBalanceHealthPhysicianAssigneeByNpi(section: Section): string | null {
  return getBalanceHealthPhysicianAssigneeForNpi(getReceivingProviderNpi(section));
}

const BALANCE_HEALTH_PHYSICIAN_NAME_INDEX = buildPhysicianNameIndex(
  Object.values(BALANCE_HEALTH_PROVIDERS_BY_NPI).map(({ physician }) => physician)
);

// Canonical roster assignee for an extracted/resolved "Family, Given" name (see matchPhysicianByName
// for the matching rule). Used when the document — or a resolved appointment provider — has no NPI.
export function getBalanceHealthPhysicianAssigneeForName(
  familyName: string | null | undefined,
  givenName: string | null | undefined
): string | null {
  return matchPhysicianByName(BALANCE_HEALTH_PHYSICIAN_NAME_INDEX, familyName, givenName);
}

// Falls back to a name match against the roster when the document has no usable NPI.
export function getBalanceHealthPhysicianAssigneeByName(section: Section): string | null {
  const reviewedContent = (section.reviewedContent ?? {}) as Record<string, unknown>;
  // Prior auth documents store the receiving provider under "practitioner"; DME orders under
  // "provider" (their schema has no receivingProvider field); all others use "receivingProvider".
  const provider = (reviewedContent.receivingProvider ?? reviewedContent.practitioner ?? reviewedContent.provider) as
    | { familyName?: string | null; givenName?: string | null }
    | undefined;

  return getBalanceHealthPhysicianAssigneeForName(provider?.familyName, provider?.givenName);
}

// LOCATION_MA: for a fax line with a per-clinic table (IFAC/FACR/…), assign by the patient's encounter
// location, falling back to the region ops manager. Lines without a table use the provider's NPI MA.
function resolveLocationMa(section: Section, faxLine: BhFaxLine, encounterLocationId: string | null): string | null {
  const table = BALANCE_HEALTH_LOCATION_MA_BY_LINE[faxLine];

  if (!table) return getMaFromSection(section) ?? resolvePhysicianName(section, faxLine);

  const queue = encounterLocationId ? table[encounterLocationId] : undefined;

  return queue ?? BALANCE_HEALTH_OPS_MANAGER_BY_LINE[faxLine] ?? null;
}

// LOCATION_FRONT_DESK: assign by encounter location for a fax line with a per-clinic front-desk table
// (Precision), else the per-line front-desk queue, else the region ops manager.
function resolveLocationFrontDesk(faxLine: BhFaxLine, encounterLocationId: string | null): string | null {
  const fallback = BALANCE_HEALTH_FRONT_DESK_BY_LINE[faxLine] ?? BALANCE_HEALTH_OPS_MANAGER_BY_LINE[faxLine] ?? null;
  const table = BALANCE_HEALTH_LOCATION_FRONT_DESK_BY_LINE[faxLine];

  if (!table) return fallback;

  return (encounterLocationId ? table[encounterLocationId] : undefined) ?? fallback;
}

function getMaFromSection(section: Section): string | null {
  const npi = getReceivingProviderNpi(section);

  if (!npi) return null;

  const provider = BALANCE_HEALTH_PROVIDERS_BY_NPI[npi];

  if (!provider) {
    logger.warn("Provider NPI not found in BALANCE_HEALTH_PROVIDERS_BY_NPI map — assignedTo will be null", {
      npi,
      sectionId: section.id,
    });

    return null;
  }

  return provider.ma ?? provider.physician;
}

function getBalanceHealthUtahDocumentInstructions(
  baseMapping: DocumentMapping,
  section: Section
): DynamicDocumentInstructions {
  return applyBalanceHealthSignatureWorkflow(
    getBalanceHealthUtahDocumentInstructionsByClassification(baseMapping, section),
    section
  );
}

// Any signature-required doc becomes a Sign task to the provider in Results - PT/OT, unless its
// classification already produced a Sign task (which keeps its tailored label). A Sign task's
// upload folder is always forced to Results - PT/OT — regardless of how the "Sign" task type was
// produced — so a future classification branch can never emit a Sign task pointed at another folder.
function applyBalanceHealthSignatureWorkflow(
  instructions: DynamicDocumentInstructions,
  section: Section
): DynamicDocumentInstructions {
  if (getAdditionalWorkflowTaskType(instructions.additionalWorkflow) === "Sign") {
    return { ...instructions, folder: "Results - PT/OT", folderMapping: "Results - PT/OT" };
  }

  const reviewedContent = (section.reviewedContent ?? {}) as Record<string, unknown>;

  if (!getBooleanField(reviewedContent, "signatureRequired")) return instructions;

  return {
    ...instructions,
    assignedTo: "Physician",
    folder: "Results - PT/OT",
    folderMapping: "Results - PT/OT",
    additionalWorkflow: ["Task Type: Sign", `Task Details: ${instructions.documentLabel}`],
  };
}

function getBalanceHealthUtahDocumentInstructionsByClassification(
  baseMapping: DocumentMapping,
  section: Section
): DynamicDocumentInstructions {
  const reviewedContent = (section.reviewedContent ?? {}) as Record<string, unknown>;
  const classification = section.uploadClassification || section.classification;
  const maAssignedTo = getMaFromSection(section);

  switch (classification) {
    case Classification.ADMIN_DISABILITY_FORMS: {
      if (matchesType(reviewedContent, "Handicap Request")) {
        return {
          ...baseMapping,
          assignedTo: maAssignedTo,
          documentLabel: "Handicap Placard Req.",
          additionalWorkflow: ["Task Type: Review", "Task Details: Handicap Placard Req."],
        };
      }

      return { ...baseMapping, assignedTo: maAssignedTo };
    }

    case Classification.ADMIN_FMLA_FORMS:
      return { ...baseMapping, assignedTo: maAssignedTo };

    case Classification.ADMIN_WORK_OR_SCHOOL_PHYSICAL_FORMS: {
      if (matchesType(reviewedContent, "Status")) {
        return {
          ...baseMapping,
          assignedTo: maAssignedTo,
          folder: "Disability",
          folderMapping: "Disability",
          documentLabel: "Work Status Req.",
          additionalWorkflow: ["Task Type: Review", "Task Details: Work Status Req."],
        };
      }

      if (matchesType(reviewedContent, "Comp")) {
        return {
          ...baseMapping,
          assignedTo: null,
          folder: "Disability",
          folderMapping: "Disability",
          documentLabel: "Worker's Comp Req.",
          additionalWorkflow: null,
        };
      }

      return baseMapping;
    }

    case Classification.FINANCIAL: {
      const financialMatch = matchFinancialType(reviewedContent);

      if (financialMatch) {
        return {
          ...baseMapping,
          assignedTo: "Billing Inquiries",
          folder: null,
          folderMapping: null,
          documentLabel: financialMatch.label,
          additionalWorkflow: [
            "Task Type: Fax",
            `Task Details: ${financialMatch.label}`,
            "Note: do not upload to chart",
          ],
        };
      }

      return baseMapping;
    }

    case Classification.FINANCIAL_ELIGIBILITY_OF_BENEFITS: {
      if (matchesType(reviewedContent, "Response")) {
        return {
          ...baseMapping,
          assignedTo: null,
          folder: "Authorization",
          folderMapping: "Authorization",
          documentLabel: "Verification of Benefits and Eligibility & Benefits",
          additionalWorkflow: null,
        };
      }

      if (matchesType(reviewedContent, "Request")) {
        return {
          ...baseMapping,
          assignedTo: null,
          folder: "Authorization",
          folderMapping: "Authorization",
          documentLabel: "Insurance Verification Req.",
          additionalWorkflow: null,
        };
      }

      return baseMapping;
    }

    case Classification.LAB_RESULTS: {
      const labType = (
        getStringField(reviewedContent, "labType") ??
        getStringField(reviewedContent, "typeOfLab") ??
        getStringField(reviewedContent, "nameOfLab") ??
        ""
      ).toLowerCase();
      const padAliases = ["peripheral arterial disease", "ankle-brachial index", "pulse volume recording", "abi"];

      if (padAliases.some((alias) => labType.includes(alias))) {
        return {
          ...baseMapping,
          assignedTo: null,
          folder: "PADnet",
          folderMapping: "PADnet",
          documentLabel: "PADnet Results",
          additionalWorkflow: null,
        };
      }

      return baseMapping;
    }

    case Classification.ORDER_LAB:
      return { ...baseMapping, assignedTo: maAssignedTo };

    case Classification.LAB_RESULTS_PATHOLOGY: {
      if (matchesType(reviewedContent, "Surgical")) {
        return {
          ...baseMapping,
          assignedTo: "Physician",
          folder: "Lab Documents",
          folderMapping: "Lab Documents",
          documentLabel: "Final Sx Pathology Report",
          additionalWorkflow: ["Task Type: Review", "Task Details: Final Sx Pathology Report"],
        };
      }

      return { ...baseMapping, assignedTo: maAssignedTo };
    }

    case Classification.MEDICAL_HISTORY_HOME_HEALTH: {
      const docType = getStringField(reviewedContent, "documentType");
      const sigRequired = getBooleanField(reviewedContent, "signatureRequired");

      const signedHomeHealthMapping: DynamicDocumentInstructions = {
        ...baseMapping,
        assignedTo: "Physician",
        folder: "Results - PT/OT",
        folderMapping: "Results - PT/OT",
        documentLabel: "Home Health Care, Plan of Care",
        additionalWorkflow: ["Task Type: Sign", "Task Details: Home Health Care, Plan of Care"],
      };

      if (!docType) {
        return sigRequired ? signedHomeHealthMapping : baseMapping;
      }

      const lower = docType.toLowerCase();

      if (lower === "plan of care") {
        if (sigRequired) {
          return {
            ...baseMapping,
            assignedTo: "Physician",
            folder: "Results - PT/OT",
            folderMapping: "Results - PT/OT",
            documentLabel: "Home Health Care, Plan of Care",
            additionalWorkflow: ["Task Type: Sign", "Task Details: Home Health Care, Plan of Care"],
          };
        }

        return {
          ...baseMapping,
          assignedTo: null,
          folder: "Chart Documents",
          folderMapping: "Chart Documents",
          documentLabel: "Home Health Care, Plan of Care",
          additionalWorkflow: null,
        };
      }

      if (lower === "progress note") {
        if (sigRequired) {
          return {
            ...baseMapping,
            assignedTo: "Physician",
            folder: "Results - PT/OT",
            folderMapping: "Results - PT/OT",
            documentLabel: "Home Health Care, Progress Note",
            additionalWorkflow: ["Task Type: Sign", "Task Details: Home Health Care, Progress Note"],
          };
        }

        return {
          ...baseMapping,
          assignedTo: null,
          folder: "Chart Documents",
          folderMapping: "Chart Documents",
          documentLabel: "Home Health Care, Progress Note",
          additionalWorkflow: null,
        };
      }

      if (lower === "rx") {
        if (sigRequired) {
          return {
            ...baseMapping,
            assignedTo: "Physician",
            folder: "Results - PT/OT",
            folderMapping: "Results - PT/OT",
            documentLabel: "Home Health Care, Approval Req.",
            additionalWorkflow: ["Task Type: Sign", "Task Details: Home Health Care, Approval Req."],
          };
        }

        return {
          ...baseMapping,
          assignedTo: null,
          folder: "Orders",
          folderMapping: "Orders",
          documentLabel: "Home Health Care, Approval Req.",
          additionalWorkflow: null,
        };
      }

      return sigRequired ? signedHomeHealthMapping : baseMapping;
    }

    case Classification.MEDICAL_RECORD_REQUEST: {
      // Assignee is overridden per fax line by the routing table (LOCATION_MEDICAL_RECORDS);
      // this default covers the no-route fallback.
      const schedulingPriority = getStringField(reviewedContent, "schedulingPriority");
      const isStat = schedulingPriority === SchedulingPriority.STAT;
      const isUrgent = schedulingPriority === SchedulingPriority.URGENT;

      const docLabel = isStat
        ? "STAT Medical Records Req."
        : isUrgent
          ? "Urgent Medical Records Req."
          : (baseMapping.documentLabel ?? "Medical Records Req.");
      const additionalWorkflow =
        isStat || isUrgent
          ? (["Task Type: Fax", `Task Details: ${docLabel}`] as readonly string[])
          : baseMapping.additionalWorkflow;

      return {
        ...baseMapping,
        assignedTo: BALANCE_HEALTH_MEDICAL_RECORDS_BY_LINE[BhFaxLine.UT] ?? null,
        documentLabel: docLabel,
        additionalWorkflow,
      };
    }

    case Classification.NOTIFICATION: {
      const specialty = getStringField(reviewedContent, "specialty")?.toLowerCase().trim() ?? "";

      if (specialty === Specialty.RADIOLOGY.toLowerCase()) {
        return {
          ...baseMapping,
          assignedTo: null,
          folder: "Radiology Report",
          folderMapping: "Radiology Report",
          documentLabel: "Radiology Confirmation Notice",
          additionalWorkflow: null,
        };
      }

      if (specialty === Specialty.WOUND_CARE.toLowerCase()) {
        return {
          ...baseMapping,
          assignedTo: null,
          documentLabel: "Wound Care, Shipment Notice",
          additionalWorkflow: null,
        };
      }

      if (specialty === Specialty.SURGERY.toLowerCase()) {
        return {
          ...baseMapping,
          assignedTo: "Physician",
          folder: "Surgical Documents",
          folderMapping: "Surgical Documents",
          documentLabel: "Sx Schd. Confirmation",
          additionalWorkflow: ["Task Type: Review", "Task Details: Sx Schd. Confirmation"],
        };
      }

      return baseMapping;
    }

    case Classification.OTHER: {
      const otherMatch = matchOtherType(reviewedContent);

      if (otherMatch) {
        return { ...baseMapping, ...otherMatch };
      }

      return baseMapping;
    }

    case Classification.PHARMACY_RELATED: {
      const pharmacyMatch = matchPharmacyType(reviewedContent);

      if (pharmacyMatch) {
        return { ...baseMapping, ...pharmacyMatch };
      }

      return baseMapping;
    }

    case Classification.PHARMACY_RELATED_REFILL: {
      const refillMatch = matchPharmacyRefillType(reviewedContent, maAssignedTo);

      if (refillMatch) {
        return { ...baseMapping, ...refillMatch };
      }

      return baseMapping;
    }

    case Classification.PRIOR_AUTHORIZATION_MEDICATION_REQUEST:
      return { ...baseMapping, assignedTo: maAssignedTo };

    case Classification.PRIOR_AUTHORIZATION_MEDICATION_RESPONSE:
      return { ...baseMapping, assignedTo: maAssignedTo };

    case Classification.PRIOR_AUTHORIZATION_PROCEDURE_REQUEST: {
      if (matchesType(reviewedContent, "MRI")) {
        return {
          ...baseMapping,
          assignedTo: "UT COB Surgery",
          folder: "Authorization",
          folderMapping: "Authorization",
          documentLabel: "MRI Auth. Req.",
          additionalWorkflow: ["Task Type: Authorization", "Task Details: MRI Auth. Req."],
        };
      }

      return baseMapping;
    }

    case Classification.PRIOR_AUTHORIZATION_PROCEDURE_RESPONSE: {
      const procMatch = matchPriorAuthProcedureType(reviewedContent, maAssignedTo);

      if (procMatch) {
        return { ...baseMapping, ...procMatch };
      }

      return { ...baseMapping, assignedTo: maAssignedTo };
    }

    case Classification.PROGRESS_NOTE: {
      const specialty = getStringField(reviewedContent, "specialty")?.toLowerCase().trim() ?? "";
      const sigRequired = getBooleanField(reviewedContent, "signatureRequired");
      const docType = getStringField(reviewedContent, "documentType")?.toLowerCase().trim() ?? "";

      if (specialty === Specialty.PHYSICAL_THERAPY.toLowerCase()) {
        if (sigRequired) {
          return {
            ...baseMapping,
            assignedTo: "Physician",
            folder: "Results - PT/OT",
            folderMapping: "Results - PT/OT",
            documentLabel: "Physical Therapy, Action Needed",
            additionalWorkflow: ["Task Type: Sign", "Task Details: Physical Therapy, Action Needed"],
          };
        }

        if (docType === "tlc pain management") {
          return {
            ...baseMapping,
            folder: "Chart Documents",
            folderMapping: "Chart Documents",
            documentLabel: "TLC Pain MGMT",
            additionalWorkflow: null,
          };
        }

        return {
          ...baseMapping,
          folder: "Results - PT/OT",
          folderMapping: "Results - PT/OT",
          documentLabel: "Signed Physical Therapy Notes",
          additionalWorkflow: null,
        };
      }

      if (specialty === Specialty.OCCUPATIONAL_THERAPY.toLowerCase()) {
        if (sigRequired) {
          return {
            ...baseMapping,
            assignedTo: "Physician",
            folder: "Results - PT/OT",
            folderMapping: "Results - PT/OT",
            documentLabel: "Occupational Therapy, Action Needed",
            additionalWorkflow: ["Task Type: Sign", "Task Details: Occupational Therapy, Action Needed"],
          };
        }

        return {
          ...baseMapping,
          folder: "Results - PT/OT",
          folderMapping: "Results - PT/OT",
          documentLabel: "Occupational Therapy Notes",
          additionalWorkflow: null,
        };
      }

      if (specialty === Specialty.WOUND_CARE.toLowerCase()) {
        return {
          ...baseMapping,
          assignedTo: "Physician",
          folder: "Results - PT/OT",
          folderMapping: "Results - PT/OT",
          documentLabel: "VAC Wound Care, Progress Notes",
          additionalWorkflow: ["Task Type: Sign", "Task Details: VAC Wound Care, Progress Notes"],
        };
      }

      if (sigRequired) {
        return {
          ...baseMapping,
          assignedTo: "Physician",
          folder: "Results - PT/OT",
          folderMapping: "Results - PT/OT",
          documentLabel: "Progress Note, Action Needed",
          additionalWorkflow: ["Task Type: Sign", "Task Details: Progress Note, Action Needed"],
        };
      }

      return baseMapping;
    }

    case Classification.REFERRAL: {
      const insurance = getStringField(reviewedContent, "insurance")?.toLowerCase() ?? "";
      const payer = getStringField(reviewedContent, "payer")?.toLowerCase() ?? insurance;

      if (payer.includes("veterans affairs") || payer === "va" || payer.startsWith("va ")) {
        return {
          ...baseMapping,
          documentLabel: "VA Referral and Forms",
          additionalWorkflow: ["Task Type: Schedule", "Task Details: VA Referral and Forms"],
        };
      }

      if (payer.includes("tricare")) {
        return {
          ...baseMapping,
          documentLabel: "Tricare Authorization",
          additionalWorkflow: ["Task Type: Schedule", "Task Details: Tricare Authorization"],
        };
      }

      return baseMapping;
    }

    default:
      return baseMapping;
  }
}

export function getBalanceHealthFaxLocationMapping(faxRecipientNumber: string): FaxLocationMapping | null {
  const staffLocation = BALANCE_HEALTH_FACILITY_NAMES[faxRecipientNumber] ?? null;

  if (!staffLocation) return null;

  return {
    referralLocation: null,
    authLocation: null,
    staffLocation,
    faxRecipientNumber,
  };
}

export function shouldBalanceHealthForwardFileUnconditionally(): boolean {
  return false;
}

export function shouldBalanceHealthAutoCloseTask(classification: Classification): boolean {
  return classification === Classification.JUNK;
}

export const BALANCE_HEALTH_DOCUMENT_MAPPINGS = BALANCE_HEALTH_MAPPINGS;

function matchesType(reviewedContent: Record<string, unknown>, expected: string): boolean {
  return getDocType(reviewedContent) === expected.toLowerCase().trim();
}

function getDocumentType(section: Section): string | null {
  const reviewedContent = (section.reviewedContent ?? {}) as Record<string, unknown>;

  return getStringField(reviewedContent, "documentType") ?? getStringField(reviewedContent, "type");
}

export function getReceivingProviderNpi(section: Section): string | null {
  const reviewedContent = (section.reviewedContent ?? {}) as Record<string, unknown>;
  // Prior auth documents store the receiving provider under "practitioner"; DME orders under
  // "provider" (their schema has no receivingProvider field); all others use "receivingProvider".
  const provider = (reviewedContent.receivingProvider ?? reviewedContent.practitioner ?? reviewedContent.provider) as
    | { npiNumber?: string }
    | undefined;
  const npi = provider?.npiNumber;

  return typeof npi === "string" ? npi : null;
}

function getStringField(reviewedContent: Record<string, unknown>, key: string): string | null {
  const value = reviewedContent[key];

  return typeof value === "string" && value.length > 0 ? value : null;
}

function getDocType(reviewedContent: Record<string, unknown>): string {
  return (getStringField(reviewedContent, "documentType") ?? getStringField(reviewedContent, "type") ?? "")
    .toLowerCase()
    .trim();
}

function getBooleanField(reviewedContent: Record<string, unknown>, key: string): boolean {
  return reviewedContent[key] === true;
}

function matchFinancialType(reviewedContent: Record<string, unknown>): { label: string } | null {
  const labels: Record<string, string> = {
    "waiver of liability": "Waiver of Liability Statement Req.",
    "payment transmittal": "Payment Transmittal",
    "payment expiring soon": "Payment Expiring Soon",
    "explanation of payment": "Explanation of Payment",
    "explanation of benefits": "Explanation of Benefits",
    "expedited payment": "Expedited Payment",
  };
  const docType = getDocType(reviewedContent);
  const label = labels[docType];

  if (!label) {
    return null;
  }

  return { label };
}

function matchOtherType(reviewedContent: Record<string, unknown>): Partial<DocumentMapping> | null {
  const docType = getDocType(reviewedContent);

  switch (docType) {
    case "pharmacy update":
      return {
        assignedTo: "Physician",
        documentLabel: "Pharmacy Update",
        additionalWorkflow: ["Task Type: Review", "Task Details: Pharmacy Update"],
      };
    case "confirmation of fax number":
      return {
        assignedTo: ASSIGNEE_THOMPSON_SHAWN,
        documentLabel: "Confirmation of Fax Number",
        additionalWorkflow: ["Task Type: Review", "Task Details: Confirmation of Fax Number"],
      };
    case "employment verification":
      return {
        assignedTo: BALANCE_HEALTH_HR_EMAIL,
        folder: null,
        folderMapping: null,
        documentLabel: "Employment Verification",
        additionalWorkflow: [`Email: ${BALANCE_HEALTH_HR_EMAIL}`],
        workflowType: "Email",
      };
    case "optum rx":
      return {
        assignedTo: ASSIGNEE_THOMPSON_SHAWN,
        documentLabel: "Optum Rx",
        additionalWorkflow: ["Task Type: Review", "Task Details: Optum Rx"],
      };
    case "repair of building":
      return {
        assignedTo: ASSIGNEE_THOMPSON_SHAWN,
        documentLabel: "Repair of Building",
        additionalWorkflow: ["Task Type: Review", "Task Details: Repair of Building"],
      };
    case "selling printer":
      return {
        assignedTo: ASSIGNEE_THOMPSON_SHAWN,
        documentLabel: "Selling Printer",
        additionalWorkflow: ["Task Type: Review", "Task Details: Selling Printer"],
      };
    case "physician information verification form":
      return {
        assignedTo: ASSIGNEE_THOMPSON_SHAWN,
        documentLabel: "Physician Information Verification Form",
        additionalWorkflow: ["Task Type: Review", "Task Details: Physician Information Verification Form"],
      };
    case "marketing documents":
      return {
        assignedTo: "Harshbarger, Denise",
        documentLabel: "Marketing Documents",
        additionalWorkflow: ["Task Type: Review", "Task Details: Marketing Documents"],
      };
    case "patient not found":
      return {
        assignedTo: ASSIGN_TO_PHYSICIAN,
        documentLabel: "Patient not Found in ModMed",
        additionalWorkflow: ["Task Type: Review", "Task Details: Patient not Found in ModMed"],
      };
    case "other":
      return {
        assignedTo: ASSIGN_TO_PHYSICIAN,
        documentLabel: "General Notification",
        additionalWorkflow: ["Task Type: Review", "Task Details: General Notification"],
      };
    default:
      return null;
  }
}

function matchPharmacyType(reviewedContent: Record<string, unknown>): Partial<DocumentMapping> | null {
  const docType = getDocType(reviewedContent);

  switch (docType) {
    case "unavailable":
      return {
        assignedTo: null,
        documentLabel: "Product Backordered/Unavailable",
        additionalWorkflow: null,
      };
    case "communication":
      return {
        documentLabel: "Copy of Rx.",
        additionalWorkflow: ["Task Type: Review", "Task Details: Copy of Rx."],
      };
    case "copy of rx":
      return {
        assignedTo: null,
        documentLabel: "Message to Prescriber",
        additionalWorkflow: null,
      };
    case "scheck & siress":
      return {
        folder: "Results - PT/OT",
        folderMapping: "Results - PT/OT",
        documentLabel: "Scheck & Siress",
        additionalWorkflow: ["Task Type: Sign", "Task Details: Scheck & Siress"],
      };
    default:
      return null;
  }
}

function matchPharmacyRefillType(
  reviewedContent: Record<string, unknown>,
  maAssignedTo: string | null
): Partial<DocumentMapping> | null {
  const docType = getDocType(reviewedContent);

  switch (docType) {
    case "new":
      return { ...prescriptionRefill("New Rx Req.", "Obtain"), assignedTo: maAssignedTo };
    case "refill":
      return prescriptionRefill("Rx Refill Req.", "Obtain");
    case "90 day":
      return { ...prescriptionRefill("90 Day Rx Req.", "Obtain"), assignedTo: maAssignedTo };
    case "alternative":
      return { ...prescriptionRefill("Alternative Rx Req.", "Obtain"), assignedTo: maAssignedTo };
    case "wound vac":
      return prescriptionRefill("Wound VAC Rx Req.", "Obtain");
    case "oas":
      return {
        assignedTo: null,
        folder: "Prescription Documents",
        folderMapping: "Prescription Documents",
        documentLabel: "OAS Rx Req.",
        additionalWorkflow: null,
      };
    case "compound":
      return prescriptionRefill("Compound Rx Req.", "Review");
    case "missing info":
      return { assignedTo: null, documentLabel: "Rx Missing Info Req.", additionalWorkflow: null };
    default:
      return null;
  }
}

function prescriptionRefill(label: string, taskType: string): Partial<DocumentMapping> {
  return {
    documentLabel: label,
    additionalWorkflow: [`Task Type: ${taskType}`, `Task Details: ${label}`],
  };
}

function matchPriorAuthProcedureType(
  reviewedContent: Record<string, unknown>,
  maAssignedTo: string | null
): Partial<DocumentMapping> | null {
  const authType = getDocType(reviewedContent);

  switch (authType) {
    case "afo":
      return {
        assignedTo: maAssignedTo,
        folder: "Authorization",
        folderMapping: "Authorization",
        documentLabel: "AFO Authorization",
        additionalWorkflow: ["Task Type: Authorization", "Task Details: AFO Authorization"],
      };
    case "mri":
      return {
        assignedTo: maAssignedTo,
        folder: "Authorization",
        folderMapping: "Authorization",
        documentLabel: "Notice of {{ authorizationStatus }} (MRI)",
        additionalWorkflow: ["Task Type: Authorization", "Task Details: Notice of {{ authorizationStatus }} (MRI)"],
      };
    case "orthotics":
      return {
        assignedTo: maAssignedTo,
        folder: "Authorization",
        folderMapping: "Authorization",
        documentLabel: "Notice of {{ authorizationStatus }} (Orthotics)",
        additionalWorkflow: [
          "Task Type: Authorization",
          "Task Details: Notice of {{ authorizationStatus }} (Orthotics)",
        ],
      };
    case "surgery":
      return {
        assignedTo: "UT COB Surgery",
        folder: "Referral/Authorization Notes",
        folderMapping: "Referral/Authorization Notes",
        documentLabel: "Sx Authorization",
        additionalWorkflow: ["Task Type: Authorization", "Task Details: Sx Authorization"],
      };
    default:
      return null;
  }
}
